"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HlsJsPlayer = void 0;
const adapter_1 = require("../../api/adapter");
const feature_1 = require("../../api/adapter/features/feature");
const error_1 = require("../../api/error");
const bitrate_switching_1 = require("../../api/player/features/bitrate-switching");
const feature_2 = require("../../api/player/features/feature");
const video_element_1 = require("../../api/typings/video-element");
const errors_1 = require("../errors");
const errors_deprecated_1 = require("../errors-deprecated");
const browser_support_1 = require("../utils/browser-support");
const play_logs_1 = require("../utils/debug/play-logs");
const stats_1 = require("../utils/stats");
const core_1 = require("./core");
const helper_1 = require("./helper");
const SKIP_FORWARD_THRESHOLD = 25;
const NOT_LOADED_ERROR = "Hls.js is not loaded";
const noop = (...args) => undefined;
class HlsJsPlayer extends core_1.CorePlayer {
    static async isSupported(logger) {
        return (0, browser_support_1.supportsHlsjs)(logger);
    }
    get hostEl() {
        // temporary hack to get the host element for HlsJs
        // since it works with the video element directly
        if (this.elementSupervisor == null) {
            return null;
        }
        return Reflect.get(this.elementSupervisor, "element");
    }
    async isSupported() {
        return HlsJsPlayer.isSupported();
    }
    static get format() {
        return "mp4-hls";
    }
    get format() {
        return HlsJsPlayer.format;
    }
    async ready() {
        await this.device.loadHlsScript(this.options.hlsjsPath);
        await super.ready();
    }
    async handleSource(manifest) {
        var _a, _b, _c, _d;
        if (this.suspended) {
            return;
        }
        const hadSubstitute = ((_b = (_a = this.manifestJson) === null || _a === void 0 ? void 0 : _a.formats["mp4-hls"]) === null || _b === void 0 ? void 0 : _b.substitute) != null;
        this.manifestJson = manifest;
        const format = manifest === null || manifest === void 0 ? void 0 : manifest.formats["mp4-hls"];
        if (format == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("Manifest did not receive a 'mp4-hls' format, the driver was not provided.", {
                manifest,
            }));
            return;
        }
        this.source = this.manifest;
        const hasSubstitute = ((_c = manifest === null || manifest === void 0 ? void 0 : manifest.formats["mp4-hls"]) === null || _c === void 0 ? void 0 : _c.substitute) != null;
        /**
         * Check if manifest changed from blurredStream to standardStream (presence of `substitute` property),
         * and that it's not the player's first source event.
         */
        if (hasSubstitute !== hadSubstitute && !this._firstSourceEv) {
            this.ctx.logger.debug("Changing stream to/from blurredStream. Restart hlsjs.");
            this.restart(true);
            return;
        }
        if (this._firstSourceEv) {
            this._firstSourceEv = false;
        }
        if (this.hostEl != null &&
            this.source != null &&
            (!this.hostEl.src ||
                this.hostEl.srcObject != null ||
                this.qualityHasChanged ||
                (adapter_1.device.isImplements(adapter_1.Feature.URL_LOCATION) && this.hostEl.src === adapter_1.device.location))) {
            const currentTime = this.hostEl.currentTime;
            this.qualityHasChanged = false;
            // make sure there are no other sources left
            this.hostEl.srcObject = null;
            // keep current time for recorded videos
            if (((_d = this.manifestJson) === null || _d === void 0 ? void 0 : _d.type) === "recorded" && currentTime > 0) {
                this.hostEl.currentTime = currentTime;
            }
            this.ctx.logger.debug("hlsjs: await play()", { aggregates: (0, play_logs_1.dumpVideoElement)(this.hostEl) });
            await this.play();
        }
    }
    get implementedFeatures() {
        return [feature_2.Feature.BITRATE_SWITCHING, feature_2.Feature.MUTED_AUTOPLAY];
    }
    get device() {
        if (this._device != null) {
            return this._device;
        }
        if (!adapter_1.device.isImplements(feature_1.Feature.HLSJS)) {
            throw new Error("Device is not supported");
        }
        this._device = adapter_1.device;
        return this._device;
    }
    constructor(ctx, provider, options) {
        super(ctx, provider, options);
        this.lastProgress = 0;
        this.manifestJson = null;
        this.destroyed = false;
        this.m3u8ManifestLoadId = 0;
        this.indexManifestLoadId = 0;
        this.initialFragmentLoadId = 0;
        this.isStarting = false;
        this._firstSourceEv = true;
        // -----
        this.lastFrag = null;
        this.loadingHls = false;
        this.loadFailure = null;
        this.recoverDecodingErrorDate = 0;
        this.recoverSwapAudioCodecDate = 0;
        this.hls = null;
        this.isAttached = false;
        this._device = null;
        this.inactivePageEventListener = false;
        this.chosenLevel = null;
        this.hlsJsInitId = stats_1.stats.start(stats_1.STATS_EVENTS.HLSJS_INIT);
        if (!adapter_1.device.isImplements(feature_1.Feature.HLSJS)) {
            throw new Error("Device is not supported");
        }
        this.loadHls();
        this.addInnerDisposer(this.destroy);
        // avoids unhandled error from events lib
        /* eslint-disable @typescript-eslint/no-empty-function */
        this.on("error", () => { });
    }
    get data() {
        var _a, _b;
        return (_b = (_a = this.manifestJson) === null || _a === void 0 ? void 0 : _a.formats["mp4-hls"]) !== null && _b !== void 0 ? _b : null;
    }
    get mediaLoader() {
        return this.provider;
    }
    /** returns the manifest string
     * @return {string}
     */
    get manifest() {
        var _a, _b, _c, _d, _e, _f, _g;
        const format = this.data;
        if (format == null) {
            return null;
        }
        let uri;
        if (format.substitute) {
            uri = format.substitute.location;
        }
        else if ((((_a = this.currentQuality) === null || _a === void 0 ? void 0 : _a.level) === bitrate_switching_1.SourceScoreLevel.High ||
            ((_b = this.currentQuality) === null || _b === void 0 ? void 0 : _b.level) === bitrate_switching_1.SourceScoreLevel.Medium ||
            ((_c = this.currentQuality) === null || _c === void 0 ? void 0 : _c.level) === bitrate_switching_1.SourceScoreLevel.Low) &&
            ((_d = format.origin) === null || _d === void 0 ? void 0 : _d.location) != null) {
            uri = (_e = format.origin) === null || _e === void 0 ? void 0 : _e.location;
        }
        else if (((_g = (_f = this.currentQuality) === null || _f === void 0 ? void 0 : _f.layer) === null || _g === void 0 ? void 0 : _g.id) != null && this.currentQuality.layer.id !== "") {
            uri = this.currentQuality.layer.id.toString();
        }
        else {
            uri = format.manifest;
        }
        if (uri == null) {
            this.ctx.logger.info("Returning null in manifest getter. Data: ", { format });
            return null;
        }
        const separator = !uri.includes("?") ? "?" : "&";
        uri += separator;
        const lastAutoKbps = this.device.globals.get("lv_auto_last_kbps");
        if (lastAutoKbps != null) {
            const nextSeparator = !uri.includes("?") ? "?" : "&";
            uri += `${nextSeparator}kbps=${lastAutoKbps}`;
        }
        return uri;
    }
    /**
     * @throws {ErrorCode.ManifestError} if manifest cannot be loaded
     */
    async reloadAndRestart() {
        if (this.isDisposed) {
            return;
        }
        await this.mediaLoader.load();
        if (this.mediaLoader.currentState === "online") {
            await this.restart(false);
        }
    }
    loadHls(inputCb) {
        const cb = inputCb !== null && inputCb !== void 0 ? inputCb : noop;
        this.device
            .loadHlsScript(this.options.hlsjsPath)
            .then(() => cb())
            .catch((err) => {
            this.ctx.logger.error("hlsjs not loaded", { path: this.options.hlsjsPath, err });
            cb(err);
        });
    }
    handleVisibilityChange() {
        if (this.device.hidden) {
            return;
        }
        const m = this.manifest;
        if (m == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.ManifestNotFoundError("cannot play, manifest is null", {}));
            return;
        }
        this.startIt(m);
    }
    async startIt(m) {
        if (!this.device.isHlsLoaded()) {
            throw new Error(helper_1.ERRORS.HLSJS_NOT_LOADED);
        }
        if (this.isDisposed || this.isStarting || m == null) {
            return;
        }
        if (!this.device.isHlsLoaded()) {
            throw new Error(NOT_LOADED_ERROR);
        }
        this.isStarting = true;
        this.device.removeEventListener("visibilitychange", this.handleVisibilityChange);
        this.inactivePageEventListener = false;
        const enableHlsWorker = () => {
            // Implementer option takes precedence
            if (this.options.enableWorker != null) {
                return this.options.enableWorker;
            }
            if (this.options.workerPath != null) {
                return true;
            }
            if (process.env.HLSJS_BUNDLED === "false") {
                return true;
            }
            // default behavior is false (default behavior in HLS is true, so we need to overwrite this);
            // Bundled HLSJS causes issues with Web Worker
            return false;
        };
        const authHeader = await this.getAuthHeader();
        const requestHeaders = { ...authHeader, ...this.options.requestHeaders };
        const hlsjsConfig = {
            // abrController: AbrController,
            debug: this.options.debug,
            maxBufferLength: this.options.maxBufferLength,
            maxBufferSize: this.options.maxBufferSize,
            fragLoadingTimeOut: 3900,
            maxBufferHole: 2,
            backBufferLength: 90,
            progressive: false,
            enableWorker: enableHlsWorker(),
            workerPath: this.options.workerPath,
        };
        this.logger.debug("creating new Hls instance", { hlsjsConfig });
        // eslint-disable-next-line no-multi-assign
        const hls = (this.hls = new this.device.Hls({
            ...hlsjsConfig,
            ...this.options.hlsjsConfig,
            xhrSetup(xhr, url) {
                if (requestHeaders != null) {
                    Object.entries(requestHeaders).forEach(([key, value]) => {
                        xhr.setRequestHeader(key, value);
                    });
                }
            },
        }));
        this.logger.debug("new hls instance created", {
            options: hlsjsConfig,
        });
        this.listen();
        // logger.setPlayer((this.mediaLoader as any).host, (this.mediaLoader as any).publicId, this.format, m, this);
        hls.loadSource(m);
        hls.attachMedia(this.hostEl);
        if (!this.mediaLoader.vod) {
            hls.on(this.device.Hls.Events.BUFFER_APPENDED, () => {
                if (this.hostEl == null || this.hostEl.buffered.length === 0 || this.hostEl.paused) {
                    return;
                }
                if (this.hostEl.buffered.end(this.hostEl.buffered.length - 1) - this.hostEl.currentTime >
                    SKIP_FORWARD_THRESHOLD) {
                    this.ctx.logger.warn(`player fell behind more than ${SKIP_FORWARD_THRESHOLD}s, restarting`);
                    this.reloadAndRestart();
                }
            });
        }
        hls.on(this.device.Hls.Events.MEDIA_ATTACHED, this.handleMediaAttached);
        hls.on(this.device.Hls.Events.MEDIA_DETACHED, () => {
            this.isAttached = false;
        });
        hls.on(this.device.Hls.Events.MANIFEST_PARSED, () => {
            this.counters.lastProgress = Date.now();
        });
        this.isStarting = false;
    }
    async playNow() {
        this.logger.debug("playNow()");
        const m = this.manifest;
        if (!this.device.isHlsLoaded()) {
            throw new Error(helper_1.ERRORS.HLSJS_NOT_LOADED);
        }
        if (m == null && this.mediaLoader.currentState === "online") {
            this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("manifest doesn't contains 'flv-http' format", {
                manifest: m,
                loader: this.mediaLoader,
            }));
            return false;
        }
        if (m == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.ManifestNotFoundError("cannot play, manifest is null", {}));
            return false;
        }
        if (this.hostEl == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.ElementRequiredError("cannot play, no video element", {}));
            return false;
        }
        if (this.hls != null) {
            // bug: probably a bug
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (this.mediaLoader.vod && m === this.hls.url) {
                try {
                    await super.play();
                }
                catch (err) {
                    const inner = err instanceof Error ? err : null;
                    this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("vod play failed in playNow()", {
                        inner,
                        player: this,
                    }));
                }
                return true;
            }
            this.destroyHls();
        }
        if (this.device.hidden) {
            if (!this.inactivePageEventListener) {
                this.device.addEventListener("visibilitychange", this.handleVisibilityChange);
                this.inactivePageEventListener = true;
            }
        }
        else {
            await this.startIt(m);
        }
        return !this.hostEl.paused;
    }
    handleMediaAttached() {
        if (this.hostEl != null && !this.localVideoPaused) {
            try {
                super.play(true);
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed in startIt() method", {
                    inner,
                    player: this,
                }));
            }
        }
        this.isAttached = true;
    }
    /**
     * Attaches the hls video player and plays the video
     * @param {function} [cb] called on play
     * @return {void}
     */
    async play() {
        this.ctx.logger.debug("play()");
        if (this.hostEl == null) {
            return false;
        }
        if (this.hls != null && !this.localVideoPaused) {
            try {
                await this.playNow();
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("playNow() failed", {
                    inner,
                    player: this,
                }));
            }
            return true;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.hostEl.onprogress = null;
        await this.ready();
        try {
            await this.playNow();
        }
        catch (err) {
            const inner = err instanceof Error ? err : null;
            this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed", {
                inner,
                player: this,
            }));
        }
        return true;
    }
    get autoLevelEnabled() {
        var _a, _b;
        return (_b = (_a = this.hls) === null || _a === void 0 ? void 0 : _a.autoLevelEnabled) !== null && _b !== void 0 ? _b : false;
    }
    /**
     * @param {number} bitrate
     * @return {void}
     */
    set bitrate(bitrate) {
        if (this.hostEl == null) {
            return;
        }
        if (adapter_1.device.isFirefox || this.mediaLoader.vod) {
            const ct = this.hostEl.currentTime;
            this.stop();
            if (this.mediaLoader.vod) {
                const { hostEl } = this;
                const onLoadMetadata = () => {
                    var _a;
                    (_a = this.hostEl) === null || _a === void 0 ? void 0 : _a.removeEventListener("loadedmetadata", onLoadMetadata);
                    hostEl.currentTime = ct;
                };
                this.hostEl.addEventListener("loadedmetadata", onLoadMetadata);
            }
            this.play().catch((err) => {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed after changing bitrate", {
                    inner,
                    player: this,
                }));
            });
        }
    }
    /**
     * stops playback
     * @return {void}
     */
    stop() {
        if (this.hls == null) {
            return;
        }
        this.destroyHls();
    }
    /**
     * stops playback
     * @return {void}
     */
    pause() {
        var _a;
        (_a = this.hostEl) === null || _a === void 0 ? void 0 : _a.pause();
    }
    /**
     * Destroys driver
     * @return {void}
     */
    destroy() {
        this.destroyed = true;
        // this.driverReady = false;
        this.destroyHls();
    }
    /**
     * Destroys HLS Driver
     */
    destroyHls() {
        const { hls } = this;
        if (hls == null) {
            return;
        }
        if (this.hostEl != null) {
            this.hostEl.removeEventListener("error", this._handleElError);
        }
        hls === null || hls === void 0 ? void 0 : hls.off(this.device.Hls.Events.ERROR, this._handleHlsErrors);
        hls.off(this.device.Hls.Events.MEDIA_ATTACHED, this.handleMediaAttached);
        hls === null || hls === void 0 ? void 0 : hls.detachMedia();
        hls === null || hls === void 0 ? void 0 : hls.destroy();
        this.hls = null;
        this.loadFailure = null;
    }
    async recoverFromBufferSeekOverHole() {
        if (this.data == null) {
            return;
        }
        this.stop();
        try {
            await this.play();
        }
        catch (err) {
            const inner = err instanceof Error ? err : null;
            this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed after recoverFromBufferSeekOverHole()", {
                inner,
                player: this,
            }));
        }
    }
    /**
     * @param {number} [bitrate] bitrate, defaults to options.bitrate || options.estimatedKbps
     * @return {object} encoding object
     */
    pickEncoding(bitrate) {
        if (this.data == null) {
            return null;
        }
        if (bitrate != null) {
            this.options.bitrate = bitrate;
        }
        if (this.options.bitrate == null) {
            return null;
        }
        const { encodings } = this.data;
        if (encodings.length === 0 || this.chosenLevel == null) {
            return null;
        }
        return encodings[this.chosenLevel];
    }
    ifNoProgress(cb) {
        const el = this.hostEl;
        if (el == null) {
            return;
        }
        const ct = el.currentTime;
        adapter_1.device.setTimeout(() => {
            if (el.currentTime <= ct) {
                cb();
            }
        }, 250);
    }
    emitProgress() {
        if (adapter_1.device.isImplements(feature_1.Feature.DEBUGGING)) {
            this.lastProgress = adapter_1.device.performance.now();
        }
        else {
            this.lastProgress = Date.now();
        }
        this.emit("progress");
    }
    /**
     * Listen for important events and record or signal
     */
    listen() {
        var _a, _b, _c, _d;
        const { hls } = this;
        const el = this.hostEl;
        if (hls == null) {
            throw new Error(helper_1.ERRORS.HLSJS_NOT_LOADED);
        }
        if (el == null) {
            throw new Error(helper_1.ERRORS.ELEMENT_REQUIRED);
        }
        hls.on(this.device.Hls.Events.ERROR, this._handleHlsErrors);
        el.addEventListener("progress", () => {
            this.emitProgress();
        });
        el.addEventListener("ended", () => {
            this.emit("ended");
        });
        const statInfo = {
            driver: this.format,
            abr: (_a = this.manifestJson) === null || _a === void 0 ? void 0 : _a.abr,
            aor: (_b = this.manifestJson) === null || _b === void 0 ? void 0 : _b.aor,
            atr: (_c = this.manifestJson) === null || _c === void 0 ? void 0 : _c.atr,
            rep: (_d = this.manifestJson) === null || _d === void 0 ? void 0 : _d.rep,
        };
        core_1.timeupdateWrapper.wrap(el, () => {
            this.emit("timeupdate");
            if (this.initialFragmentLoadId !== -1) {
                const isVideoPlaying = el.currentTime > 0 && !el.paused && !el.ended && el.readyState > 2;
                if (isVideoPlaying) {
                    this.emit("initialFragmentLoad", stats_1.stats.stop(this.initialFragmentLoadId, statInfo));
                    this.initialFragmentLoadId = -1;
                }
            }
        });
        el.addEventListener("error", this._handleElError);
        hls.on(this.device.Hls.Events.MANIFEST_LOADING, (ev, data) => {
            this.emit("hlsJsInit", stats_1.stats.stop(this.hlsJsInitId, statInfo));
            this.m3u8ManifestLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.M3U8_MANIFEST_LOAD);
        });
        hls.on(this.device.Hls.Events.MANIFEST_LOADED, (ev, data) => {
            this.emit("m3u8ManifestLoad", stats_1.stats.stop(this.m3u8ManifestLoadId, statInfo));
            this.indexManifestLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.INDEX_MANIFEST_LOAD);
        });
        hls.once(this.device.Hls.Events.LEVEL_UPDATED, (ev, data) => {
            this.emit("indexManifestLoad", stats_1.stats.stop(this.indexManifestLoadId, statInfo));
        });
        hls.once(this.device.Hls.Events.FRAG_LOADING, (ev) => {
            this.initialFragmentLoadId = stats_1.stats.start(stats_1.STATS_EVENTS.INITIAL_FRAGMENT_LOAD);
        });
        hls.on(this.device.Hls.Events.FRAG_LOADED, (ev, data) => {
            this.loadedFrag(data.frag);
        });
    }
    _handleHlsErrors(type, data) {
        var _a;
        let ct = 0;
        const el = this.hostEl;
        let curLevel;
        try {
            switch (data.details) {
                case this.device.Hls.ErrorDetails.LEVEL_LOAD_ERROR:
                case this.device.Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
                    if (this.mediaLoader.vod) {
                        const getData = (0, helper_1.dataProperties)(data);
                        this.emitErrorDeprecated(new errors_deprecated_1.PlaybackError("hlsjs playback error", { ...getData, loader: this.mediaLoader }));
                        return;
                    }
                    if (this.mediaLoader.currentState === "offline") {
                        this.stop();
                        return;
                    }
                    this.ctx.logger.warn("level load reload and restart", { type: String(type) });
                    this.reloadAndRestart().catch((err) => {
                        const msg = (0, error_1.isVideoClientError)(err) && err.code === errors_deprecated_1.ErrorCode.ManifestNotLoaded
                            ? "hlsjs restart failed because manifest has not loaded"
                            : "hlsjs restart failed";
                        this.emitError((0, errors_1.createError)(errors_deprecated_1.ErrorCode.PlayerLoadingFailed, msg, { player: HlsJsPlayer.displayName, format: this.format }, err));
                    });
                    if (el != null) {
                        ct = el.currentTime;
                        adapter_1.device.setTimeout(() => {
                            if (el.currentTime <= ct) {
                                this.ctx.logger.warn("level load reload and restart", { type: String(type) });
                                this.reloadAndRestart().catch((err) => {
                                    const msg = (0, error_1.isVideoClientError)(err) && err.code === errors_deprecated_1.ErrorCode.ManifestNotLoaded
                                        ? "hlsjs restart failed because manifest has not loaded"
                                        : "hlsjs restart failed";
                                    this.emitError((0, errors_1.createError)(errors_deprecated_1.ErrorCode.PlayerLoadingFailed, msg, { player: HlsJsPlayer.displayName, format: this.format }, err));
                                });
                            }
                        }, 100);
                        return;
                    }
                    break;
                case this.device.Hls.ErrorDetails.BUFFER_SEEK_OVER_HOLE:
                    this.ifNoProgress(() => {
                        this.recoverFromBufferSeekOverHole();
                    });
                    return;
                case this.device.Hls.ErrorDetails.BUFFER_APPEND_ERROR:
                case this.device.Hls.ErrorDetails.BUFFER_FULL_ERROR:
                    this.counters.bufferOverflowCount++;
                    break;
                case this.device.Hls.ErrorDetails.FRAG_LOAD_ERROR:
                case this.device.Hls.ErrorDetails.FRAG_DECRYPT_ERROR:
                case this.device.Hls.ErrorDetails.FRAG_PARSING_ERROR:
                case this.device.Hls.ErrorDetails.BUFFER_APPENDING_ERROR:
                case this.device.Hls.ErrorDetails.BUFFER_STALLED_ERROR:
                    this.counters.bufferUnderflowCount++;
                    break;
                case this.device.Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
                    // this.ctx.logger.error("unable to load m3u8 manifest", { level: curLevel, url: data.url });
                    this.ctx.logger.error("unable to load m3u8 manifest", { url: data.url });
                    // it'll restart the player
                    this.setPreferredLevel(bitrate_switching_1.TranscodeScoreLevel.Highest);
                    return;
                default:
                    break;
            }
            switch (data.type) {
                case this.device.Hls.ErrorTypes.NETWORK_ERROR:
                    // Everything but frag timeout errors, manifest errors, etc
                    // the player is busted and needs to retry
                    // either rely on the retry protocol of the hls driver,
                    // or just hard restart it
                    // for live it seems the same either way
                    if (data.details !== this.device.Hls.ErrorDetails.FRAG_LOAD_TIMEOUT) {
                        this.counters.currentErrorCount++;
                    }
                    else {
                        this.counters.currentErrorCount = (_a = this.options.recoverErrorCount) !== null && _a !== void 0 ? _a : 0;
                    }
                    this.emitErrorDeprecated(new errors_deprecated_1.NetworkError("hlsjs playback error", { data: (0, helper_1.dataProperties)(data) }));
                    break;
                case this.device.Hls.ErrorTypes.MEDIA_ERROR:
                    if (data.fatal) {
                        this.handleMediaError();
                        return;
                    }
                    // don't add to current error count for buffer stalls
                    if (data.details === this.device.Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
                        this.emitErrorDeprecated(new errors_deprecated_1.PlaybackError("hlsjs media error", { data: (0, helper_1.dataProperties)(data), loader: this.mediaLoader }));
                        return;
                    }
                    if (data.details === this.device.Hls.ErrorDetails.FRAG_LOAD_ERROR) {
                        // Its caused when the client sees the same segment multiple times
                        // when the manifest file is being loaded several times,
                        // each seem to spawn segment loads
                        // many segment loads then compete and cause these errors
                        // its probably just an indicator of a screwed state
                        // Tracking it and reloading after a recovery count
                        this.counters.currentErrorCount++;
                    }
                    this.emitErrorDeprecated(new errors_deprecated_1.PlaybackError("hlsjs media error", { data: (0, helper_1.dataProperties)(data), loader: this.mediaLoader }));
                    break;
                case this.device.Hls.ErrorTypes.OTHER_ERROR:
                    this.counters.currentErrorCount++;
                    this.ctx.logger.warn("hlsjs other error", (0, helper_1.dataProperties)(data));
                    break;
                default:
                    this.ctx.logger.error("unhandled hlsjs error", (0, helper_1.dataProperties)(data));
                    return;
            }
            this.checkRestart(data);
            const getData = (0, helper_1.dataProperties)(data);
            this.ctx.logger.warn(`${type}`, getData);
        }
        catch (err) {
            const inner = err instanceof Error ? err : null;
            this.emitErrorDeprecated(new errors_deprecated_1.HandleHlsJsError("error handling error from hlsjs library", { inner }));
        }
    }
    _handleElError(e) {
        var _a;
        if (!(0, video_element_1.isVideoElement)(e.target) || e.target.error == null) {
            return;
        }
        switch (e.target.error.code) {
            case video_element_1.MediaErrorCodeConstants.MEDIA_ERR_DECODE:
                this.handleMediaError();
                break;
            case video_element_1.MediaErrorCodeConstants.MEDIA_ERR_SRC_NOT_SUPPORTED:
                this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("hlsjs src not supported", {
                    manifest: this.manifest,
                    loader: this.mediaLoader,
                }));
                break;
            default:
                break;
        }
        if (this.autoPlay) {
            (_a = this.hostEl) === null || _a === void 0 ? void 0 : _a.play();
        }
    }
    handleMediaError() {
        const now = this.device.isImplements(feature_1.Feature.DEBUGGING) ? this.device.performance.now() : Date.now();
        const { hls } = this;
        if (hls == null) {
            if (this.destroyed || this.isDisposed) {
                return;
            }
            throw new Error(helper_1.ERRORS.HLSJS_NOT_LOADED);
        }
        if (this.recoverDecodingErrorDate != null || now - this.recoverDecodingErrorDate > 3000) {
            this.recoverDecodingErrorDate = now;
            hls.recoverMediaError();
        }
        else if (this.recoverSwapAudioCodecDate != null || now - this.recoverSwapAudioCodecDate > 3000) {
            this.recoverSwapAudioCodecDate = now;
            hls.swapAudioCodec();
            hls.recoverMediaError();
        }
        this.ifNoProgress(() => {
            this.recoverFromBufferSeekOverHole();
        });
    }
    get currentLevel() {
        const { hls } = this;
        if (hls == null) {
            return null;
        }
        if (hls.abrController.fragCurrent == null) {
            return null;
        }
        return hls.abrController.fragCurrent.level;
    }
    get nextLevel() {
        const { hls } = this;
        if (hls == null) {
            throw new Error(helper_1.ERRORS.HLSJS_NOT_LOADED);
        }
        return hls.abrController.nextAutoLevel;
    }
    bitrateSwitch(level) {
        var _a;
        if (this.hls == null) {
            return;
        }
        if (level > ((_a = this.currentLevel) !== null && _a !== void 0 ? _a : 0)) {
            this.counters.upshift++;
        }
        else {
            this.counters.downshift++;
        }
        this.emit("bitrate-switch");
    }
    loadedFrag(inputFrag) {
        var _a, _b, _c;
        const frag = inputFrag;
        this.counters.fragCounts++;
        // bug: suspicious line. Fragment request doesn't exists and isn't using anywhere else
        frag.request =
            this.lastFrag != null ? frag.trequest - ((_b = (_a = this.lastFrag) === null || _a === void 0 ? void 0 : _a.trequest) !== null && _b !== void 0 ? _b : 0) : frag.trequest;
        this.lastFrag = frag;
        this.counters.fragSize += frag.loaded;
        this.counters.fragDuration += frag.duration;
        this.counters.fragDownloadTime += frag.request;
        this.counters.fragMaxTime = Math.max(this.counters.fragMaxTime, frag.request);
        this.counters.fragMinTime = Math.min((_c = this.counters.fragMinTime) !== null && _c !== void 0 ? _c : frag.request, frag.request);
    }
}
exports.HlsJsPlayer = HlsJsPlayer;
HlsJsPlayer.displayName = "HlsJsPlayer";
//# sourceMappingURL=hlsjs.js.map